import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {OrganizationService} from "../../../../../service-data/organization.service";
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Util} from "../../../../../util/Util";
import {ProjectService, CreateProjectRequest} from "../../../../../service-data/project.service";
import {LanguagePipe} from "../../../../../pipe/language.pipe";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrls: ['./create-project.component.css']
})
export class CreateProjectComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  public newProjectName: string;
  public changeForm: FormGroup;
  wasFormChanged = false;
  organization: Organization;
  selectedFolder: string;
  projectFolders: ProjectFolders;
  validProjectName: boolean = false;

  /**
   * Seed a Draft inside the new Project and open it for editing.
   *
   * Default ON. A Project with nothing in it is a dead end — the
   * operator's next action is always "put a book in it", and the
   * only way to do that from scratch is a Draft with an interior
   * and a cover PDF. Ticking this maps to `seedEmptyDraft` on the
   * consolidated endpoint, and the returned Draft id is handed back
   * to the caller so it can drop the operator straight into the
   * Draft editor (title/author fields + the two PDF uploads).
   *
   * Unticking it produces a bare Project, which is what the
   * "organize existing Books and Drafts into Projects" flows want.
   */
  createDraft: boolean = true;

  /** True while the create request is in flight — blocks double submits. */
  creating: boolean = false;

  /** Server-side failure text, shown inline so the dialog can stay open. */
  errorMessage: string = '';

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public organizationService: OrganizationService, private projectService: ProjectService,  @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<CreateProjectComponent>) {
    // Get data passed in
    if ( data.organization ) {
      this.organization = data.organization;
      this.projectFolders = this.organization.getProjectFolders();
      this.selectedFolder = data.selectedFolder;
      this.newProjectName = '';
    }
  }

  public ngOnInit(): void {
    const dirNameRegex = /^(?!(?:CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:\.[^.]*)?$)[^<>:"/\\|?*\x00-\x1F]*[^<>:"/\\|?*\x00-\x1F .]$/;
    this.changeForm = this.formBuilder.group({
      newProjectName: [this.newProjectName, [Validators.required, Validators.pattern(dirNameRegex)]]
    });
    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code

    this.changeForm.get('newProjectName').valueChanges.pipe(debounceTime(300)) // Wait 300ms after the last keystroke
      .subscribe(() => this.update());
  }

  public onChange(): void {
    if ( this.creating ) {
      return;
    }
    this.markAsDirty(this.changeForm);
    this.creating = true;
    this.errorMessage = '';

    // Consolidated endpoint rather than the legacy
    // `createnewproject/{name}/{folder}` call. Two reasons:
    // `seedEmptyDraft` makes the Draft OPTIONAL (legacy always
    // seeded one), and the response carries `project_ID` /
    // `seededDraft_ID`, which is what lets the caller select the
    // Project and open its Draft without guessing by name.
    //
    // `folder` is NORMALIZED. It used to be `this.selectedFolder || '/'`,
    // which persisted the root as '/' -- the tree's spelling -- while
    // `Project.folder` defaults to '' and `MoveProjectComponent` writes ''.
    // The server stores whatever it is given, so a Project created at the
    // root landed in a third state: `isProjectInFolder` normalizes and so
    // still showed it, but the raw grid filter and the per-folder counts
    // key off the string and read '/' as neither the root nor any real
    // folder. Same normalization as the move path, so the two agree.
    const req: CreateProjectRequest = {
      name: this.newProjectName,
      folder: Util.normalizeFolderPath(this.selectedFolder),
      seedEmptyDraft: this.createDraft
    };
    this.projectService.createProject(req).subscribe(
      envelope => {
        this.creating = false;
        if ( !envelope || envelope.status !== 'SUCCESS' ) {
          this.errorMessage = (envelope && envelope.message) || LanguagePipe.get('Create Project failed. Please try again.');
          console.error('CreateProject returned a non-SUCCESS envelope', envelope);
          return;
        }
        const data = envelope.data;
        if ( data && data.warnings && data.warnings.length > 0 ) {
          // The Project exists; only sub-steps failed. Proceed —
          // the Projects screen is where the operator can see and
          // fix whatever did not land.
          console.warn('CreateProject completed with warnings', data.warnings);
        }
        console.log(`Create Project completed ${this.selectedFolder} ${this.newProjectName}`);
        this.dialogRef.close({
          created: true,
          projectName: this.newProjectName,
          project_ID: data ? data.project_ID : 0,
          // `primaryDraft_ID` is set when the server made the seeded
          // Draft primary; `seededDraft_ID` is the raw id it created.
          // Either is a valid thing to open.
          primaryDraft_ID: data ? (data.primaryDraft_ID || data.seededDraft_ID || 0) : 0,
          openDraft: this.createDraft
        });
      },
      err => {
        // Deliberately NOT swallowed and deliberately NOT retried
        // through the legacy endpoint: a failed response does not
        // prove the Project was not created server-side, and a
        // blind retry is how duplicates get made. Report it and let
        // the operator decide.
        this.creating = false;
        this.errorMessage = LanguagePipe.get('Create Project failed. Please try again.');
        console.error(`Create Project error ${this.selectedFolder} ${this.newProjectName}`, err);
      }
    );
  }

  public update(): void {
    this.newProjectName = this.changeForm.value['newProjectName'];
    this.validProjectName = this.isValidProjectName(this.newProjectName);
    this.markAsDirty(this.changeForm);
  }

  private isValidProjectName(projectName: string): boolean {
    if ( !projectName ) {
      return false;
    }
    this.projectService.objects.forEach( x => { if ( x.name === projectName ) { return false; }});
    return true;
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  private markAsDirty(group: FormGroup): void {
    group.markAsDirty();
    // tslint:disable-next-line:forin
    for (const i in group.controls) {
      group.controls[i].markAsDirty();
    }
  }

  formChanged() {
    this.wasFormChanged = true;
  }
}
